import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {PearlHarborSoundConfig} from "./PearlHarborSoundConfig";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {PearlHarborHelper} from "../utils/PearlHarborHelper";
import {IWinRatioConfig, PearlHarborWinConfigs} from "../configs/PearlHarborWinConfigs";
import {gsap, TweenLite} from "gsap";
import {NolimitHowl} from "@nolimitcity/slot-audio/bin/NolimitHowl";
import {NolimitAudioPlayer} from "@nolimitcity/slot-audio/bin/NolimitAudioPlayer";
import {PearlHarbor} from "../PearlHarbor";
import {PearlHarborData} from "../server/PearlHarborData";
import {PearlHarborGameMode} from "../server/PearlHarborGameMode";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {NolimitConfig} from "@nolimitcity/slot-launcher/bin/settings/NolimitConfig";

export class PearlHarborSoundManager {
    public static symExplodeSoundPlayed:boolean = false;
    public static rockExplodeSoundPlayed:boolean = false;
    public static countUpEndSoundPlayed:boolean = false;

    public static avalancheReelCounter:number = 0;
    private static collapseReelLandSoundPlayed:boolean = false;
    private static avalancheReelLandSoundPlayed:boolean = false;

    private static currentLoop:string = "";
    private static countUpStartSoundPlayed:boolean = false;
    private static bcUpdateSoundPlayed:boolean = false;
    private static scatterToWildSoundPlayed:boolean = false;
    private static multUpSoundPlayed:boolean = false;
    private static wildLandingSoundPlayed:boolean = false;
    private static wildExplodeSoundPlayed:boolean = false;
    private static scatterLandingInPlayed:boolean = false;
    private static scatterLandingOutPlayed:boolean = false;
    private static anticipationCompleteSoundPlayed:boolean = false;
    private static scatterMultiplierUpdatePlayed:boolean = false;
    private static bombMultiplierUpdatePlayed:boolean = false;
    private static targetToWildPlayed:boolean = false;
    private static splitWildPlayed:boolean = false;
    private static splitSlicePlayed:boolean = false;
    private static normalScatterCount:number = 0;
    private static bigwinLevelSound:string = "";
    private static isBigWinEnd:boolean = true;

    private static _init:boolean = false;
    private static _meterSpinStartSoundId:number = -1;
    private static _meterHitSoundId:number = -1;

    private static get soundPlayer():NolimitAudioPlayer {
        if(!this._init && NolimitConfig.isDevMode) {
            (<NolimitAudioPlayer>SlotGame.sound).showLogs(true);
            this._init = true;
        }
        return <NolimitAudioPlayer>SlotGame.sound;
    }

    public static resetSoundsOnGameData():void {
        this.symExplodeSoundPlayed = false;
        this.countUpStartSoundPlayed = false;
        this.bcUpdateSoundPlayed = false;
        this.scatterToWildSoundPlayed = false;
        this.multUpSoundPlayed = false;
        this.wildLandingSoundPlayed = false;
        this.wildExplodeSoundPlayed = false;
        this.scatterLandingInPlayed = false;
        this.scatterLandingOutPlayed = false;
        this.anticipationCompleteSoundPlayed = false;
        this.rockExplodeSoundPlayed = false;
        this.scatterMultiplierUpdatePlayed = false;
        this.bombMultiplierUpdatePlayed = false;
        this.collapseReelLandSoundPlayed = false;
        this.avalancheReelLandSoundPlayed = false;
        this.targetToWildPlayed = false;
        this.splitWildPlayed = false;
        this.splitSlicePlayed = false;
    }

    public static playAmbientLoop(duration:number = 200, name:string = "", volume:number = 1):void {
        name = name.length > 0 ? name : this.getAmbientSoundName();

        if(!PearlHarborHelper.isGameStarted) {
            name = PearlHarborSoundConfig.MAIN_GAME_AMBIANCE;
        }

        if(this.currentLoop === name) {
            return;
        }

        if(this.currentLoop) {
            this.stopAmbientLoop();
            this.playAmbientLoop(duration, name, volume);
        } else {
            this.currentLoop = name;
            this.soundPlayer.playTrack({name : this.currentLoop, volume : 0});
            this.soundPlayer.fadeTrack({
                name : this.currentLoop,
                from : 0,
                to : volume,
                duration : duration
            });
        }
    }

    public static stopAmbientLoop(dur:number = 200):void {
        if(this.currentLoop) {
            const ambientLoop:NolimitHowl | undefined = this.soundPlayer.fadeTrack({
                name : this.currentLoop,
                to : 0,
                duration : dur
            });
            ambientLoop && TweenLite.to(this, {duration : (dur / 1000)}).eventCallback("onComplete", () => {
                this.stopSoundEffect(ambientLoop.soundName);
            });
            this.currentLoop = "";
        }
    }

    public static playSoundEffect(name:string, repeat?:number, volume?:number):void {
        if(this.soundPlayer.isLoaded && PearlHarborHelper.isGameStarted) {
            this.soundPlayer.playTrack({
                name : name,
                repeat : repeat,
                volume : volume
            });
        }
    }

    public static stopSoundEffect(name:string):void {
        this.soundPlayer.stopTrack(name);
    }

    public static playCountUpStart():void {
        const soundName:string = PearlHarborSoundConfig.COUNT_UP;
        this.playSoundEffect(soundName, 10000, 1);
        this.countUpStartSoundPlayed = true;
    }

    public static playFSOutroCountUpEnds():void {
        this.stopSoundEffect(PearlHarborSoundConfig.COUNT_UP);
        this.playSoundEffect(PearlHarborSoundConfig.COUNT_UP_END[3]);
    }

    public static playCountUpEnds():void {
        const winRatioConfig:IWinRatioConfig = PearlHarbor.totalWinView.getWinRatioConfig(),
            winType:string = winRatioConfig.type;

        // #137
        if(PearlHarborHelper.isWinBelowStake()) {
            return;
        } else {
            if(this.countUpStartSoundPlayed) {
                this.stopSoundEffect(PearlHarborSoundConfig.COUNT_UP);
                this.countUpStartSoundPlayed = false;
            }
        }

        if(!this.countUpEndSoundPlayed) {
            this.countUpEndSoundPlayed = true;

            if(winType === PearlHarborWinConfigs.TINY) {
                this.playSoundEffect(PearlHarborSoundConfig.COUNT_UP_END[0]);
            } else if(winType === PearlHarborWinConfigs.SMALL) {
                this.playSoundEffect(PearlHarborSoundConfig.COUNT_UP_END[1]);
            } else if(winType === PearlHarborWinConfigs.MEDIUM) {
                this.playSoundEffect(PearlHarborSoundConfig.COUNT_UP_END[2]);
            } else if(winType === PearlHarborWinConfigs.LARGE) {
                this.playSoundEffect(PearlHarborSoundConfig.COUNT_UP_END[3]);
            }
        }
    }

    public static playReelLandingAll(reelId:number):void {
        if(reelId > 0) {
            return;
        }
        this.playSoundEffect(PearlHarborSoundConfig.LANDING_ALL);
    }

    public static playReelCollapseLanding():void {
        if(!this.collapseReelLandSoundPlayed) {
            this.playSoundEffect(this.avalancheReelCounter > 1 ? PearlHarborSoundConfig.LANDING_FEW : PearlHarborSoundConfig.LANDING_ONE);
            this.collapseReelLandSoundPlayed = true;
        }
    }

    public static playReelAvalancheLanding():void {
        if(!this.avalancheReelLandSoundPlayed) {
            this.playSoundEffect(this.avalancheReelCounter > 1 ? PearlHarborSoundConfig.LANDING_FEW : PearlHarborSoundConfig.LANDING_ONE);
            this.avalancheReelLandSoundPlayed = true;
        }
    }

    public static playHighlightWinSound():void {
        this.playSoundEffect(PearlHarborSoundConfig.HIGHLIGHT);
    }

    public static playWinExplodeSound():void {
        const winRatioConfig:IWinRatioConfig = PearlHarbor.totalWinView.getWinRatioConfig(),
            winType:string = winRatioConfig.type;

        if(winType === PearlHarborWinConfigs.TINY) {
            this.playSoundEffect(PearlHarborSoundConfig.EXPLODE_TINY_SMALL);
        } else if(winType === PearlHarborWinConfigs.SMALL) {
            this.playSoundEffect(PearlHarborSoundConfig.EXPLODE_TINY_SMALL);
        } else if(winType === PearlHarborWinConfigs.MEDIUM) {
            this.playSoundEffect(PearlHarborSoundConfig.EXPLODE_MEDIUM);
        } else {
            this.playSoundEffect(PearlHarborSoundConfig.EXPLODE_LARGE);
        }
    }

    public static playBoostOffSound():void {
        this.playSoundEffect(PearlHarborSoundConfig.ENHANCE_BET_OFF);
    }

    public static stopBoostOffSound():void {
        //this.stopSoundEffect(PearlHarborSoundConfig.ENHANCE_BET_OFF);
    }

    public static stopBoostOnSound():void {
        //this.stopSoundEffect(PearlHarborSoundConfig.ENHANCE_BET_1);
        //this.stopSoundEffect//(PearlHarborSoundConfig.ENHANCE_BET_2);
    }

    public static playBoostOnSound(isLevelOne:boolean):void {
        this.playSoundEffect(isLevelOne ? PearlHarborSoundConfig.ENHANCE_BET_1 : PearlHarborSoundConfig.ENHANCE_BET_2);
    }

    public static playXSSymbolLandingSound(isActive:boolean):void {
        //this.playSoundEffect(PearlHarborSoundConfig.HIGHLIGHT_TINY_SMALL);
    }

    public static playXSToWildSound():void {
        if(!this.splitWildPlayed) {
            this.playSoundEffect(PearlHarborSoundConfig.X_SPLIT_WILD);
            this.splitWildPlayed = true;
        }
    }

    public static playTargetToWild():void {
        if(!this.targetToWildPlayed) {
            this.playSoundEffect(PearlHarborSoundConfig.TARGET_TO_WILD);
            this.targetToWildPlayed = true;
        }
    }

    public static playXSSplitSound():void {
        if(!this.splitSlicePlayed) {
            this.playSoundEffect(PearlHarborSoundConfig.X_SPLIT_SLICE[MathHelper.randomIntInRange(0, 1)]);
            this.splitSlicePlayed = true;
        }
    }

    public static playReelIn():void {
        this.playSoundEffect(PearlHarborSoundConfig.REELS_IN);
    }

    public static playReelOut():void {
        this.playSoundEffect(PearlHarborSoundConfig.REELS_OUT);
    }

    public static playCoinCountupSound(value:number):void {

    }

    public static fadeMusic(volume:number, duration:number, isStart:boolean):void {
        // debugger;
        this.soundPlayer.fadeTrack({
            name : this.currentLoop,
            to : volume,
            duration : duration
        });
    }

    public static playBigWinSound(id:number):void {
        this.isBigWinEnd = false;
        this.stopBigWinSound();
        this.bigwinLevelSound = PearlHarborSoundConfig.BIG_WIN_LEVELS[id - 1];
        this.playSoundEffect(PearlHarborSoundConfig.BIG_WIN_LEVELS[id - 1]);
    }

    public static playBigWinEndSound():void {
        this.stopBigWinSound();
        this.playSoundEffect(PearlHarborSoundConfig.BIG_WIN_END);
        this.isBigWinEnd = true;
    }

    public static stopBigWinSound():void {
        if(this.bigwinLevelSound) {
            this.stopSoundEffect(this.bigwinLevelSound);
            this.bigwinLevelSound = "";
        } else {
            Logger.logDev("sound error this.bigwinLevelSound is blank", this.bigwinLevelSound);
        }
    }

    public static reelAreaChangeSound(reset:boolean = false, withAnimation:boolean = false):void {
        if(reset) {
            this.playSoundEffect(PearlHarborSoundConfig.REEL_AREA_SHRINK);
        } else if(withAnimation) {
            this.playSoundEffect(PearlHarborSoundConfig.REEL_AREA_EXPAND);
        }
    }

    public static playScatterAnticipation():void {
        //this.playSoundEffect(PearlHarborSoundConfig.SCATTER_ANTICIPATION);
    }

    public static playScatterTrigger():void {
        this.normalScatterCount = 0;
        this.playSoundEffect(PearlHarborSoundConfig.SCATTER_WIN);
    }

    public static fadeAndStopScatterAnticipation():void {
        //const anticipationSound: NolimitHowl | undefined = this.soundPlayer.fadeTrack({
        //    name: PearlHarborSoundConfig.SCATTER_ANTICIPATION,
        //    to: 0,
        //    duration: 100
        //});
        //anticipationSound && TweenLite.to(this, {duration: 0.1}).eventCallback("onComplete", () => {
        //    this.stopSoundEffect(anticipationSound.soundName);
        //});
    }

    public static playScatterAnticipationComplete():void {
        this.fadeAndStopScatterAnticipation();
        if(!this.anticipationCompleteSoundPlayed) {
            //this.playSoundEffect(PearlHarborSoundConfig.SCATTER_ANTICIPATION_DONE);
            this.anticipationCompleteSoundPlayed = true;
        }
    }

    public static playScatterAnticipationFail():void {
        this.fadeAndStopScatterAnticipation();
        //this.playSoundEffect(PearlHarborSoundConfig.SCATTER_ANTICIPATION_FAIL);
    }

    public static playBigWinSkipSound():void {
        //this.playSoundEffect(PearlHarborSoundConfig.BIGWIN_SKIP);
    }

    public static playMultUpSound():void {
        if(!this.multUpSoundPlayed) {
            //this.playSoundEffect(PearlHarborSoundConfig.MULTIPLIER_UP);
            this.multUpSoundPlayed = true;
        }
    }

    public static playContinueBtnSound():void {
        //this.playSoundEffect(PearlHarborSoundConfig.CONTINUE_BTN);
    }

    public static playBCUpdateSound():void {
        if(!this.bcUpdateSoundPlayed) {
            //this.playSoundEffect(PearlHarborSoundConfig.BC_COLLECTS);
            this.bcUpdateSoundPlayed = true;
        }
    }

    public static onNewBet():void {
        this.normalScatterCount = 0;
    }

    public static playIdleSound(symbolName:string, isActivated:boolean):void {
        //console.log(`play sound effect: ${symbolName}Idle${isActivated ? "" : "Outside"}`);
    }

    public static playBombMultiplierUpdate():void {
        if(!this.bombMultiplierUpdatePlayed) {
            this.playSoundEffect(PearlHarborSoundConfig.MULTIPLIER_BOMB);
            this.bombMultiplierUpdatePlayed = true;
        }
    }

    public static playScatterMultiplierUpdate():void {
        if(!this.scatterMultiplierUpdatePlayed) {
            this.playSoundEffect(PearlHarborSoundConfig.MULTIPLIER_SCATTER);
            this.scatterMultiplierUpdatePlayed = true;
        }
    }

    public static playBombDropExplodeSound():void {
        if(!this.wildExplodeSoundPlayed) {
            this.playSoundEffect(PearlHarborSoundConfig.X_BOMB_DROP);
            this.wildExplodeSoundPlayed = true;
        }
    }

    public static playBombLandingSound():void {
        if(!this.wildLandingSoundPlayed) {
            this.playSoundEffect(PearlHarborSoundConfig.X_BOMB_LANDING);
            this.wildLandingSoundPlayed = true;
        }
    }

    public static playXSLandingSound():void {
        //this.playSoundEffect(PearlHarborSoundConfig.SPLI);
    }

    public static playScatterLandingSound(isActive:boolean, isMiddleScatter:boolean = false):void {
        if(isActive) {
            if(!this.scatterLandingInPlayed) {
                const isBoostedBet1Scatter:boolean = PearlHarbor.foregroundElements.boostedBet && isMiddleScatter;
                this.playSoundEffect(PearlHarborSoundConfig.SCATTER_LANDING_IN, 0, isBoostedBet1Scatter?0.8:1);
                this.scatterLandingInPlayed = true;
            }
        } else {
            if(!this.scatterLandingOutPlayed) {
                this.playSoundEffect(PearlHarborSoundConfig.SCATTER_LANDING_OUT);
                this.scatterLandingOutPlayed = true;
            }
        }
        this.normalScatterCount++;
        this.normalScatterCount = Math.min(this.normalScatterCount, (PearlHarbor.model.scatterCount - 1));
    }

    public static playMeterSpinStart():void {
        let id:number = Math.floor(Math.random() * 3);
        while(id === this._meterSpinStartSoundId) {
            id = Math.floor(Math.random() * 3);
        }
        this._meterSpinStartSoundId = id;
        this.playSoundEffect(PearlHarbor.apiPlugin.settings.get(APISetting.FAST_SPIN) ?
            PearlHarborSoundConfig.FS_SPIN_START_FAST[this._meterSpinStartSoundId] :
            PearlHarborSoundConfig.FS_SPIN_START[this._meterSpinStartSoundId]);
        this.playSoundEffect(PearlHarborSoundConfig.FS_SPIN, 100);
    }

    public static playMeterSpinStop():void {
        // this.stopSoundEffect(PearlHarborSoundConfig.FS_SPIN_START[this._meterSpinStartSoundId]);
        this.stopSoundEffect(PearlHarborSoundConfig.FS_SPIN);
        this.playSoundEffect(PearlHarborSoundConfig.FS_SPIN_STOP);
        // this.playSoundEffect(PearlHarborSoundConfig.FS_SPIN_END[this._meterSpinStartSoundId]);
    }

    public static playMeterHit(hitNum:number):void {
        if(hitNum > 1) {
            this.playSoundEffect(PearlHarborSoundConfig.FS_SPIN_HIT[3]);
            return;
        }
        let id:number = Math.floor(Math.random() * 3);
        while(id === this._meterHitSoundId) {
            id = Math.floor(Math.random() * 3);
        }
        this._meterHitSoundId = id;
        this.playSoundEffect(PearlHarborSoundConfig.FS_SPIN_HIT[this._meterHitSoundId]);
    }

    public static playSymLandingSound(reelId:number):void {
        //const soundName: string = PearlHarborSoundConfig.SYM_LANDING.next();
        //const sound: NolimitHowl | undefined = this.soundPlayer.playTrack(this.createConfig(soundName));
        //if (sound) {
        //    Logger.logDev("sound: starting Sound", soundName, "for reel:", reelId);
        //    sound.player.stereo(this.davidPanningReelStop[reelId]);
        //}
    }

    public static onFsIntroShow():void {
        this.stopAmbientLoop(1);
        this.playAmbientLoop(1, PearlHarborSoundConfig.FS_INTRO);
        this.playSoundEffect(PearlHarborSoundConfig.SPLASH);
    }

    public static onFsIntroClose():void {
        this.stopAmbientLoop(1);
        this.soundPlayer.playTrack({name : PearlHarborSoundConfig.FS_LOOP});
        this.soundPlayer.playTrack({name : PearlHarborSoundConfig.FS_DOGFIGHT, volume : 0});
        this.soundPlayer.playTrack({name : PearlHarborSoundConfig.FS_BOMB_ATTACK, volume : 0});
        this.currentLoop = PearlHarborSoundConfig.FS_LOOP;
    }

    public static onFsBomAttackAnimationStart():void {
        this.playSoundEffect(PearlHarborSoundConfig.FS_BOMB_ATTACK_ANIMATION);
    }

    public static onFsBomAttackHitOrMiss(hit:boolean):void {
        this.playSoundEffect(hit ? PearlHarborSoundConfig.FS_BA_HIT : PearlHarborSoundConfig.FS_BA_MISS);
    }

    public static onFsKamikazeAnimationStart():void {
        this.playSoundEffect(PearlHarborSoundConfig.FS_KAMIKAZE);
    }

    public static onFsKamikazeHitOrMiss(hit:boolean):void {
        this.playSoundEffect(hit ? PearlHarborSoundConfig.FS_KAMIKAZE_HIT : PearlHarborSoundConfig.FS_KAMIKAZE_MISS);
    }

    public static onFsDogFightAnimationStart(dir:number):void {
        this.playSoundEffect(PearlHarborSoundConfig.FS_DOGFIGHT_PLANE[dir], undefined, 1);
    }

    public static onFsDogFightHit(hit:boolean, dir:number):void {
        if(!hit) {
            return;
        }
        this.soundPlayer.fadeTrack({name : PearlHarborSoundConfig.FS_DOGFIGHT_PLANE[dir], to : 0, duration : 500});
        this.playSoundEffect(PearlHarborSoundConfig.FS_DOGFIGHT_HIT);
    }

    public static onFsBomAttackShow():void {
        this.soundPlayer.fadeTrack({name : PearlHarborSoundConfig.FS_LOOP, to : 0, duration : 1000});
        this.soundPlayer.fadeTrack({name : PearlHarborSoundConfig.FS_DOGFIGHT, to : 0, duration : 1000});
        this.soundPlayer.fadeTrack({name : PearlHarborSoundConfig.FS_BOMB_ATTACK, to : 1, duration : 1000});
        this.currentLoop = PearlHarborSoundConfig.FS_BOMB_ATTACK;
    }

    public static onFsDogfightShow():void {
        this.soundPlayer.fadeTrack({name : PearlHarborSoundConfig.FS_LOOP, to : 0, duration : 1000});
        this.soundPlayer.fadeTrack({name : PearlHarborSoundConfig.FS_DOGFIGHT, to : 1, duration : 1000});
        this.soundPlayer.fadeTrack({name : PearlHarborSoundConfig.FS_BOMB_ATTACK, to : 0, duration : 1000});
        this.currentLoop = PearlHarborSoundConfig.FS_DOGFIGHT;
        this.playSoundEffect(PearlHarborSoundConfig.SPLASH);
    }

    public static onFsDogfightEnd():void {
        this.soundPlayer.fadeTrack({name : PearlHarborSoundConfig.FS_LOOP, to : 1, duration : 1500});
        this.soundPlayer.fadeTrack({name : PearlHarborSoundConfig.FS_DOGFIGHT, to : 0, duration : 1500});
        this.soundPlayer.fadeTrack({name : PearlHarborSoundConfig.FS_BOMB_ATTACK, to : 0, duration : 1500});
        this.currentLoop = PearlHarborSoundConfig.FS_LOOP;
    }

    public static onFsBombAttackEnd():void {
        this.soundPlayer.fadeTrack({name : PearlHarborSoundConfig.FS_LOOP, to : 1, duration : 1500});
        this.soundPlayer.fadeTrack({name : PearlHarborSoundConfig.FS_DOGFIGHT, to : 0, duration : 1500});
        this.soundPlayer.fadeTrack({name : PearlHarborSoundConfig.FS_BOMB_ATTACK, to : 0, duration : 1500});
        this.currentLoop = PearlHarborSoundConfig.FS_LOOP;
    }

    public static playFSEndSound():void {
        this.soundPlayer.stopTrack(PearlHarborSoundConfig.FS_LOOP);
        this.soundPlayer.stopTrack(PearlHarborSoundConfig.FS_DOGFIGHT);
        this.soundPlayer.stopTrack(PearlHarborSoundConfig.FS_BOMB_ATTACK);
        this.playAmbientLoop();
    }

    public static playFSEndSoundFromKamakaziToWinCap():void {
        const tl = gsap.timeline();
        tl.add(() => {
            this.soundPlayer.fadeTrack({name : PearlHarborSoundConfig.FS_LOOP, to : 0, duration : 1500});
            this.soundPlayer.fadeTrack({name : PearlHarborSoundConfig.FS_DOGFIGHT, to : 0, duration : 1500});
            this.soundPlayer.fadeTrack({name : PearlHarborSoundConfig.FS_BOMB_ATTACK, to : 0, duration : 1500});
            this.playAmbientLoop(1, PearlHarborSoundConfig.MAIN_GAME_AMBIANCE, 0);
        });
        tl.add(() => {
            this.soundPlayer.stopTrack(PearlHarborSoundConfig.FS_LOOP);
            this.soundPlayer.stopTrack(PearlHarborSoundConfig.FS_DOGFIGHT);
            this.soundPlayer.stopTrack(PearlHarborSoundConfig.FS_BOMB_ATTACK);
        }, 1.5);
    }

    public static onFsOutroContinue():void {
        if(!PearlHarbor.model.gameData.brokeBank) {
            this.playContinueBtnSound();
        }
        this.playAmbientLoop();
    }

    // #431
    public static stopSoundsInFSAbort():void {
        //if (PearlHarborHelper.isFreeSpin(PearlHarbor.model.gameData.mode)) {
        //    const soundsToCheck: string[] = [PearlHarborSoundConfig.WAGON_DWARF_COLLECT, PearlHarborSoundConfig.COUNTUP_LARGE_COIN, PearlHarborSoundConfig.COUNTUP_MID_COIN, PearlHarborSoundConfig.COUNTUP_SMALL_COIN];
//
        //    soundsToCheck.forEach((sound: string) => {
        //        if (this.soundPlayer.fetchSoundPlayer(sound)?.isPlaying) {
        //            this.stopSoundEffect(sound);
        //        }
        //    });
        //}
    }

    private static getAmbientSoundName():string {
        const gameData:PearlHarborData = PearlHarbor.model.data,
            currMode:PearlHarborGameMode = gameData.mode,
            nextMode:string = gameData.nextMode;

        let musicName:string;

        if(PearlHarborHelper.isFreeSpin(nextMode)) {
            musicName = PearlHarborSoundConfig.FS_AMBIANCE;
        } else {
            musicName = PearlHarborSoundConfig.MAIN_GAME_AMBIANCE;
        }

        return musicName;
    }
}